import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import '../../assets/css/print-page.scss';
import formatNumber from '../../utils/formatNumber';
import { getModalLine } from '../../services/analysis';

const PrintPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get data from state or URL parameters
  const stateData = location.state || {};

  const title = stateData.title || searchParams.get('title') || '粗利益 (/肉豚)';
  const fromYear = stateData.fromYear || searchParams.get('fromYear');
  const toYear = stateData.toYear || searchParams.get('toYear');
  const fromMonth = stateData.fromMonth || searchParams.get('fromMonth');
  const toMonth = stateData.toMonth || searchParams.get('toMonth');
  const groupBy = stateData.groupBy || searchParams.get('groupBy') || 'month';
  const calc = searchParams.get('calc');
  const farmLocal = searchParams.get('farm');
  const fromGraph = searchParams.get('fromGraph');

  // Chart data state
  const [lineData, setLineData] = useState(stateData.lineData || null);
  const [lineOptions, setLineOptions] = useState(stateData.lineOptions || {});
  const [isLoading, setIsLoading] = useState(!stateData.lineData);

  // Redirect to analysis page if required parameters are missing
  useEffect(() => {
    if (!stateData.lineData && (!calc || !fromYear || !toYear || !fromMonth || !toMonth)) {
      navigate('/analysis/1');
    }
  }, []);

  // Load chart data from API when accessed via URL parameters
  useEffect(() => {
    if (!stateData.lineData && calc && fromYear && toYear && fromMonth && toMonth) {
      setIsLoading(true);
      getModalLine(calc, fromYear, toYear, fromMonth, toMonth, farmLocal, groupBy, fromGraph)
        .then(({ data }) => {
          const tempData: any = {
            datasets: [],
            labels: data.axisX.map((item: any) => String(item))
          };

          setLineData(tempData);
          setLineOptions({
            plugins: {
              legend: { display: true },
              title: { display: false }
            },
            scales: {
              y: { beginAtZero: true }
            }
          });
        })
        .catch((error) => {
          console.error('Error loading chart data:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [calc, fromYear, toYear, fromMonth, toMonth, farmLocal, groupBy, fromGraph]);

  // Page size state (A4 or A3) with shared persistence
  const [pageSize, setPageSize] = useState<'A4' | 'A3'>(() => {
    const savedExpanded = localStorage.getItem('modalChart_isExpanded');
    if (savedExpanded) {
      return JSON.parse(savedExpanded) ? 'A3' : 'A4';
    }
    return stateData.isExpanded ? 'A3' : 'A4';
  });

  // Table visibility state with shared persistence
  const [showTableLocal, setShowTableLocal] = useState(() => {
    const saved = localStorage.getItem('modalChart_showTable');
    return saved ? JSON.parse(saved) : (stateData.showTable || false);
  });

  // Chart height resize state with shared persistence
  const [chartHeightLocal, setChartHeightLocal] = useState(() => {
    const saved = localStorage.getItem('modalChart_chartHeight');
    return saved ? Number(saved) : (stateData.chartHeight || 400);
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(chartHeightLocal);

  // Page width based on size
  const pageWidth = pageSize === 'A4' ? 990 : 1400;

  // Persist chart height changes
  useEffect(() => {
    localStorage.setItem('modalChart_chartHeight', String(chartHeightLocal));
  }, [chartHeightLocal]);

  // Persist page size as modal expansion state
  useEffect(() => {
    const newExpanded = pageSize === 'A3';
    localStorage.setItem('modalChart_isExpanded', JSON.stringify(newExpanded));
  }, [pageSize]);

  // Persist table visibility changes
  useEffect(() => {
    localStorage.setItem('modalChart_showTable', JSON.stringify(showTableLocal));
  }, [showTableLocal]);

  const resolveGroupBy = () => {
    switch (groupBy) {
      case 'month':
        return '単月';
      case 'quarter':
        return '四半期';
      case 'year':
        return '単年';
    }
  }

  const resolveDate = () => {
    if (!fromYear || !toYear || !fromMonth || !toMonth) {
      return '--/-- 〜 --/--';
    }

    const paddedFromMonth = fromMonth.length === 1 ? fromMonth.padStart(2, '0') : fromMonth;
    const paddedToMonth = toMonth.length === 1 ? toMonth.padStart(2, '0') : toMonth;

    return `${fromYear}/${paddedFromMonth} 〜 ${toYear}/${paddedToMonth}`;
  }

  // Chart resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = chartHeightLocal;
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = e.clientY - resizeStartY.current;
      const newHeight = resizeStartHeight.current + deltaY;

      // Clamp height between 250px and 800px
      const clampedHeight = Math.min(Math.max(newHeight, 250), 800);
      setChartHeightLocal(clampedHeight);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  const renderDataTable = () => {
    if (!lineData || !lineData.labels || !lineData.datasets) return null;

    return (
      <div className="table-responsive print-table">
        <table className="table table-striped table-bordered">
          <thead className="table-dark">
            <tr>
              <th style={{ padding: '8px 12px' }}>期間</th>
              {lineData.datasets.map((dataset: any, idx: number) => (
                <th key={idx} style={{ padding: '8px 12px' }}>{dataset.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineData.labels.map((label: string, rowIdx: number) => (
              <tr key={rowIdx}>
                <td className="fw-bold" style={{ padding: '8px 12px' }}>{label}</td>
                {lineData.datasets.map((dataset: any, colIdx: number) => (
                  <td key={colIdx} className="text-end" style={{ padding: '8px 12px' }}>
                    {dataset.data[rowIdx] !== null && dataset.data[rowIdx] !== undefined
                      ? formatNumber(dataset.data[rowIdx])
                      : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="print-page-wrapper">
      <nav className="print-page-nav" style={{ width: `${pageWidth}px`, maxWidth: `${pageWidth}px` }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          戻る
        </button>

        <div className="d-flex gap-2 align-items-center">
          {/* Botões de tamanho A4/A3 */}
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${pageSize === 'A4' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setPageSize('A4')}
              style={{ height: '38px' }}
            >
              A4
            </button>
            <button
              type="button"
              className={`btn ${pageSize === 'A3' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setPageSize('A3')}
              style={{ height: '38px' }}
            >
              A3
            </button>
          </div>

          {/* Toggle de exibição da tabela */}
          <div
            onClick={() => setShowTableLocal(!showTableLocal)}
            style={{
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              transition: 'background-color 0.2s',
              height: '38px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e9ecef';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
          >
            <span style={{ fontWeight: '500', fontSize: '14px', lineHeight: '1' }}>データ一覧表示：</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: showTableLocal ? '#0d6efd' : '#6c757d',
              color: '#fff',
              fontSize: '12px',
              transition: 'background-color 0.3s',
              lineHeight: '1',
            }}>
              {showTableLocal ? '　●' : '●　'}
            </span>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.print()}
            style={{ height: '38px' }}
          >
            印刷
          </button>
        </div>
      </nav>
      <div className="print-page-container" style={{ width: `${pageWidth}px`, maxWidth: `${pageWidth}px` }}>
        <h1>{title}</h1>
        <div className="print-page-headers">
          <h2>{resolveGroupBy()}</h2>
          <h2>{resolveDate()}</h2>
        </div>
        <div
          className="chart-container position-relative"
          style={{
            height: `${chartHeightLocal}px`,
            minHeight: '250px',
            maxHeight: '800px',
            paddingBottom: '25px'
          }}
        >
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : lineData ? (
            <Line data={lineData} options={{...lineOptions, responsive: true, maintainAspectRatio: false}} />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <p>データがありません</p>
            </div>
          )}

          {/* Resize Handle */}
          <div
            className="chart-resize-handle"
            onMouseDown={handleResizeStart}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '20px',
              cursor: 'ns-resize',
              backgroundColor: isResizing ? 'rgba(13, 110, 253, 0.1)' : 'transparent',
              transition: 'background-color 0.2s',
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onMouseEnter={(e) => {
              if (!isResizing) {
                e.currentTarget.style.backgroundColor = 'rgba(233, 236, 239, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isResizing) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <div style={{
              width: '60px',
              height: '4px',
              backgroundColor: '#6c757d',
              borderRadius: '2px',
            }} />
          </div>
        </div>

        {showTableLocal && renderDataTable()}
      </div>
    </div>
  )
}
export default PrintPage;
